import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { updateLead, updateLeadBilling } from "@/lib/api";

/**
 * Reusable edit dialog. Sections drive the field list.
 *
 * Props:
 *  - section: 'client' | 'service' | 'notes' | 'summary' | 'billing-meta'
 *  - lead: current lead document
 *  - open / onClose / onSaved
 */
export default function LeadEditDialog({ section, lead, open, onClose, onSaved }) {
  const config = SECTIONS[section] || { title: "Edit", fields: [] };
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    const initial = {};
    for (const f of config.fields) initial[f.name] = lead[f.name] ?? f.defaultValue ?? "";
    setValues(initial);
  }, [open, lead, config.fields]);

  if (!lead) return null;

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      const billingPayload = {};
      for (const f of config.fields) {
        const cur = values[f.name];
        if (f.billing) {
          billingPayload[f.name] = f.type === "number" ? parseFloat(cur || 0) : cur;
        } else {
          payload[f.name] = f.type === "list" ? (Array.isArray(cur) ? cur : (cur || "").split(",").map((s) => s.trim()).filter(Boolean)) : cur;
        }
      }
      let updated = lead;
      if (Object.keys(payload).length > 0) {
        updated = await updateLead(lead.id, payload);
      }
      if (Object.keys(billingPayload).length > 0) {
        updated = await updateLeadBilling(lead.id, billingPayload);
      }
      onSaved?.(updated);
      onClose?.();
      toast.success(`${config.title} saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className={[
          "p-0 gap-0 flex flex-col overflow-hidden border-0 sm:border",
          "w-screen h-[100dvh] max-w-none max-h-none rounded-none",
          "sm:w-[95vw] sm:h-auto sm:max-w-lg sm:max-h-[90vh] sm:rounded-lg",
        ].join(" ")}
        data-testid={`edit-dialog-${section}`}
      >
        <div className="shrink-0 px-5 sm:px-6 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg" style={{ color: "#0B2545" }}>{config.title}</DialogTitle>
            <DialogDescription>
              Changes are logged to the lead&apos;s activity history with your user ID and timestamp.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-4">
          {config.fields.map((f) => (
            <FieldInput
              key={f.name}
              field={f}
              value={values[f.name]}
              onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
            />
          ))}
        </div>

        <div className="shrink-0 border-t bg-white px-5 sm:px-6 py-3 flex items-center justify-end gap-2"
             style={{ borderColor: "#E2E8F0" }}>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-cancel">Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving}
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="edit-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
const FieldInput = ({ field, value, onChange }) => {
  if (field.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#475569" }}>
          {field.label}
        </Label>
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="h-10" data-testid={`field-${field.name}`}>
            <SelectValue placeholder={field.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt.value || opt} value={opt.value || opt}>
                {opt.label || opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#475569" }}>{field.label}</Label>
        <textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-slate-400 resize-y"
          style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  if (field.type === "list") {
    const arr = Array.isArray(value) ? value : (value || "").split(",").map((s) => s.trim()).filter(Boolean);
    return (
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#475569" }}>{field.label}</Label>
        <Input
          value={arr.join(", ")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || "Comma-separated"}
          className="h-10"
          data-testid={`field-${field.name}`}
        />
        <p className="text-[10px]" style={{ color: "#94A3B8" }}>Separate multiple values with commas.</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#475569" }}>{field.label}</Label>
      <Input
        type={field.type || "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className="h-10"
        data-testid={`field-${field.name}`}
      />
    </div>
  );
};

// =============================================================================
const URGENCY_OPTIONS = ["Standard", "Urgent", "Immediate", "Same-Day", "Routine"];
const PRICING_TYPES = ["Standard", "Rush", "Custom Payment", "Facility"];
const PAYMENT_STATUSES = [
  "unpaid", "needs_pricing", "custom_invoice_pending", "custom_payment_created",
  "payment_link_sent", "partial_payment", "paid", "confirmed_scheduled", "cancelled",
];

const SECTIONS = {
  client: {
    title: "Edit · Client Information",
    fields: [
      { name: "full_name", label: "Full name", placeholder: "Jane Doe" },
      { name: "email",     label: "Email",     type: "email", placeholder: "jane@example.com" },
      { name: "phone",     label: "Phone",     placeholder: "(732) 555-0123" },
      { name: "company",   label: "Company / Facility", placeholder: "Optional" },
      { name: "service_address", label: "Address", type: "textarea", placeholder: "Street, City, State ZIP" },
    ],
  },
  service: {
    title: "Edit · Service Details",
    fields: [
      { name: "services", label: "Services", type: "list", placeholder: "Phlebotomist Service, Drug Test, …" },
      { name: "urgency",  label: "Urgency",  type: "select", options: URGENCY_OPTIONS },
      { name: "preferred_date", label: "Preferred date", type: "date" },
      { name: "preferred_time", label: "Preferred time", type: "time" },
      { name: "service_address", label: "Service location", type: "textarea", placeholder: "Where will the service occur?" },
    ],
  },
  notes: {
    title: "Edit · Internal Notes",
    fields: [
      { name: "notes", label: "Notes", type: "textarea", placeholder: "Internal notes, prep instructions, etc." },
    ],
  },
  summary: {
    title: "Edit · Summary",
    fields: [
      { name: "services", label: "Services", type: "list" },
      { name: "preferred_date", label: "Preferred date", type: "date" },
      { name: "preferred_time", label: "Preferred time", type: "time" },
      { name: "service_address", label: "Location", type: "textarea" },
      { name: "pricing_type", label: "Pricing type", type: "select", options: PRICING_TYPES, billing: true },
      { name: "currency", label: "Currency", placeholder: "USD", billing: true },
    ],
  },
  billing: {
    title: "Edit · Billing",
    fields: [
      { name: "amount_due", label: "Amount due", type: "number", billing: true, placeholder: "0.00" },
      { name: "amount_paid", label: "Amount paid", type: "number", billing: true, placeholder: "0.00" },
      { name: "pricing_type", label: "Pricing type", type: "select", options: PRICING_TYPES, billing: true },
      { name: "payment_status", label: "Payment status", type: "select", options: PAYMENT_STATUSES, billing: true },
      { name: "custom_payment_link", label: "Payment link", placeholder: "https://...", billing: true },
    ],
  },
};
